import axios from 'axios';
import { notification } from 'antd';
import { get, throttle } from 'lodash';
import { context, createContextKey, trace, SpanStatusCode } from '@opentelemetry/api';
import { getCustomerID } from '@utils/getCustomerID';
import { checkFeature } from '@utils/platform/features/checkFeature';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import config from './config';
import { BASE } from '../../.build/configs/modules';

const redirectToLogin = () => {
  // avoid looping
  if (!window.location.href.includes('login')) {
    window.location.href = `/login?next=${window.location.pathname}${window.location.search}`;
  }
};

const instance = axios.create(config);

// base is a build output but not a product mode
if (window.conviva.module !== BASE.module) {
  axios.defaults.params = { mode: (window.conviva.module || 'EI').toLocaleLowerCase() };
}

// Create an OpenTelemetry tracer
const tracer = trace.getTracer('axios-tracer');

const endSpan = (span) => {
  span.end();
};

const addContextIdHeader = (reqConfig) => {
  const contextId = window.conviva.contextId;
  if (contextId) {
    /* eslint-disable no-param-reassign */
    reqConfig.headers['X-contextId'] = contextId;
  }
  return reqConfig;
};

const addCustomerIdHeader = (reqConfig) => {
  const cid = getCustomerID();
  if (cid) {
    /* eslint-disable no-param-reassign */
    reqConfig.headers['X-cid'] = btoa(cid);
  }
  return reqConfig;
};

const addProductContextHeader = (reqConfig) => {
  if (CURRENT_PRODUCT_MODE) {
    /* eslint-disable no-param-reassign */
    reqConfig.headers['x-context'] = CURRENT_PRODUCT_MODE;
  }
  return reqConfig;
};

const addPulsePageUrlHeader = (reqConfig) => {
  if (window.location.href) {
    reqConfig.headers['X-Pulse-Page-Url'] = window.location.href;
  }
  return reqConfig;
};

const forceReload = (response) => {
  if (response.status === 205) {
    // customer id mistmatch, reload page
    window.location.reload();
    return null;
  }
  return response;
};

// notify only once every 30s
const notifyUser = throttle(
  ({ message, description = '' }) => {
    notification.error({ message, description });
  },
  1000 * 30,
  { leading: true, trailing: false },
);

const handleErrorCodes = (error) => {
  console.error('handleErrorCodes', error);

  const status = get(error, ['response', 'status'], 0);
  const url = get(error, ['config', 'url'], '');

  if (status === 404 && url.includes('/sharing/')) {
    window.location.replace('/app');
    return Promise.reject(error);
  }

  if (status === 429) {
    notifyUser({
      message: 'Too many requests',
      description: 'Please try again after some time.',
    });
  }

  // Redirect if 401 (Unauthorized) or 403 (Forbidden)
  if (status === 401 || status === 403) {
    redirectToLogin();
  }
  return Promise.reject(error);
};

const resolveRejectErrorResponse = (error) => {
  return Promise.reject(error);
};

export function addAxiosInterceptors(axi) {
  if (checkFeature('common.header-context-id')) {
    axi.interceptors.request.use(addContextIdHeader, (error) => Promise.reject(error));
  }

  axi.interceptors.request.use(addCustomerIdHeader, (error) => Promise.reject(error));

  axi.interceptors.request.use(addProductContextHeader, (error) => Promise.reject(error));

  axi.interceptors.request.use(addPulsePageUrlHeader, (error) => Promise.reject(error));

  axi.interceptors.response.use(forceReload, handleErrorCodes);

  // Intercept request
  axi.interceptors.request.use(
    (config) => {
      // We are configurnig the span and traceparent here as it becomes available to all
      // the requests. Otherwise we would need to instrument each request individually.
      // See dimensioncontainer.ts for reference on how to correctly instrument an API.
      const span = tracer.startSpan(`HTTP ${config.method} ${config.url}`);
      span.addEvent('Request Span');
      // Add trace context to HTTP headers
      const key = createContextKey(`HTTP ${config.method} ${config.url} context`);
      const ctx = context.active();
      context.with(ctx.setValue(key, span), () => {
        const spanid = span.spanContext().spanId;
        const traceid = span.spanContext().traceId;
        // Add traceparent header based upon w3c standard
        config.headers['traceparent'] = '00-' + traceid + '-' + spanid + '-' + '00';
        if (!config.headers['x-trace-id']) {
          config.headers['x-trace-id'] = span.spanContext().traceId;
        }
      });
      // Trace is generated once span ends. This is supposed to be done in the response interceptor
      // However span context gets lost in the response interceptor. Hence we end the span here.
      // The interval provided by the span is incorrect.
      span.end();
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Intercept response to end the span. THIS DOES NOT WORK. keeping it here for reference
  axi.interceptors.response.use(
    (response) => {
      const ctx = context.active();
      const key = createContextKey(`HTTP ${response.config.method} ${response.config.url} context`);
      context.with(ctx, () => {
        const span = trace.getSpan(context.active());
        if (span) {
          endSpan(span);
        }
      });
      const span = trace.getSpan(context.active());
      if (span) {
        endSpan(span);
      }
      return response;
    },
    (error) => {
      const span = trace.getSpan(context.active());
      if (span) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: 'Error',
        });
        endSpan(span);
      }
      return resolveRejectErrorResponse(error);
    },
  );
}

addAxiosInterceptors(instance);

export default instance;
